import { jitsiLocalStorage } from '@jitsi/js-utils';
import { sha256 } from 'js-sha256';
import moment from 'moment';

import { getCurrentConference } from '../base/conference';
import { apiKEY, mtDomain } from '../base/config/constants';
import { fetchAxiosApi } from '../base/mtApi';
import { isLocalParticipantModerator } from '../base/participants';
import { checkIfReactNative, isDomainWeborNative } from '../base/util/checkOS';
import { isInBreakoutRoom } from '../breakout-rooms/functions';

import { MEETING_LIMIT_EXCEEDED_COMMAND, SET_MEETING_LIMIT_EXCEEDED } from './actionTypes';
import logger from './logger';

declare var config: Object;

/**
 * Sets whether the meeting limit is exceeded.
 *
 * @param {boolean} isExceeded - Whether the meeting limit is exceeded.
 * @param {Object} [details] - Detailed payload from API.
 * @returns {{
 *     type: string,
 *     isExceeded: boolean,
 *     details: Object
 * }}
 */
export function setMeetingLimitExceeded(isExceeded: boolean, details: Object = {}) {
    return {
        type: SET_MEETING_LIMIT_EXCEEDED,
        isExceeded,
        details
    };
}

/**
 * Verifies if the meeting license limit is exceeded by calling meeting/totalparticipants API.
 * Updates the Redux state and broadcasts to participants if local participant is moderator.
 *
 * @returns {Function}
 */
export function verifyConferenceMeetingLimit() {
    return async (dispatch: Function, getState: Function) => {
        const state = getState();
        const conference = getCurrentConference(state);

        if (!conference) {
            return true;
        }

        if (isInBreakoutRoom(state)) {
            return true;
        }

        if (state['features/base/config']?.iAmRecorder) {
            return true;
        }

        const roomName = state['features/base/conference']?.room;

        if (!roomName) {
            return true;
        }

        const { API_URL_PREFIX, API_VERSION_PREFIX, MH_CLIENT_ID } = state['features/base/config'] ?? {};
        const clientId = MH_CLIENT_ID || (typeof config === 'undefined' ? undefined : config.MH_CLIENT_ID);

        const accessToken = checkIfReactNative()
            ? state['features/base/jwt']?.jwt
            : jitsiLocalStorage.getItem('accessToken') ?? state['features/base/jwt']?.jwt;

        const moement = moment().seconds(0)
            .milliseconds(0)
            .format('X');
        const mesh256 = sha256(`${apiKEY}:MH:${moement}`);
        const roomPayload = {
            client_id: clientId,
            credentials: mesh256,
            domain: mtDomain,
            ...isDomainWeborNative,
            meeting_id: roomName
        };

        const headers = accessToken
            ? {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`
            }
            : {
                'Content-Type': 'application/json'
            };

        try {
            const participantCountObject = await fetchAxiosApi('meeting/totalparticipants', {
                method: 'post',
                headers,
                body: JSON.stringify(roomPayload),
                config: {
                    API_URL_PREFIX,
                    API_VERSION_PREFIX
                }
            });

            const {
                allow_to_join = true,
                allow_meeting_limit = 0,
                total_running_meeting = 0,
                subscription_active = false
            } = participantCountObject?.data ?? {};

            const isExceeded = !allow_to_join
                || (allow_meeting_limit > 0 && total_running_meeting > allow_meeting_limit);

            const details = {
                allow_to_join,
                allow_meeting_limit,
                total_running_meeting,
                subscription_active,
                ...participantCountObject?.data || {}
            };

            logger.info(
                `Conference meeting limit check: isExceeded=${isExceeded}, `
                + `allow_to_join=${allow_to_join}, `
                + `allow_meeting_limit=${allow_meeting_limit}, `
                + `total_running_meeting=${total_running_meeting}`
            );

            dispatch(setMeetingLimitExceeded(isExceeded, details));

            // If moderator, broadcast state to all participants via sendCommand (presence)
            if (conference && isLocalParticipantModerator(getState())) {
                try {
                    if (typeof conference.sendCommand === 'function') {
                        conference.sendCommand(MEETING_LIMIT_EXCEEDED_COMMAND, {
                            attributes: {
                                paused: String(isExceeded),
                                details: JSON.stringify(details || {}),
                                timestamp: String(Date.now())
                            }
                        });
                    }
                } catch (broadcastErr) {
                    logger.warn('Failed to broadcast MEETING_LIMIT_EXCEEDED via sendCommand', broadcastErr);
                }
            }

            return !isExceeded;
        } catch (error) {
            logger.error('Error verifying meeting limit in conference', error);

            return true;
        }
    };
}
